import { FormattedMessage } from 'react-intl';

import TimestampAndMileage from '@/features/engineoil/components/TimestampAndMileage';
import { SOURCE_DEVICE } from '@/features/engineoil/utils/constants';
import {
    getBarColorClass,
    getThresholdClass,
    isEngineOilOutdated,
    isVehicleModelOlderThan2022,
} from '@/features/engineoil/utils/utils';
import type { EngineOil } from '@/generated/GeneratedApiTypes';
import useHoverIntent from '@/hooks/useHoverIntent';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import BasePanel from './BasePanel';

interface Ocu3SsSingleCardProps {
    engineOil: EngineOil;
}

// Ocu3SsSingle = TGE vehicle (OCU3 device, sensor-signal-single reading).
// State is always unknown; only status (danger/warning/normal) is resolved from percentage.
const Ocu3SsSingleCard = ({ engineOil }: Ocu3SsSingleCardProps) => {
    const { status, measurement, measured_at, model_year } = engineOil;
    const [hoverRef] = useHoverIntent(1000, () => AnalyticsEvents.hoverSensorSignalCardOlder24h(SOURCE_DEVICE.OCU3));

    const isReadingNotPossible = isEngineOilOutdated(measured_at.date);

    const thresholdClass = getThresholdClass(status, isReadingNotPossible);
    const barColorClass = getBarColorClass(status, isReadingNotPossible);
    const textColorClass = isReadingNotPossible ? 'text-color-gray' : 'text-color-gray-darkest';
    const percentage = measurement != null ? Math.round(measurement) : 0;

    return (
        <BasePanel
            thresholdClass={!isVehicleModelOlderThan2022(model_year) ? thresholdClass : ''}
            testId="card-workshop-ocu3-sensor_signal_single"
            subTitleKey="intl-msg:engineOil.card.ssSingle.subTitle"
        >
            {!isVehicleModelOlderThan2022(model_year) ? (
                <>
                    <div className="display-flex flex-wrap">
                        <TimestampAndMileage timestamp={measured_at.date} mileage={measured_at.mileage_km} />
                    </div>

                    <div className="width-100pct display-flex align-items-center padding-top-10">
                        <div className="progress margin-0 flex-1-0" style={{ height: '10px' }}>
                            <div
                                className={`progress-bar ${barColorClass}`.trim()}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                        <div className={`margin-left-10 ${textColorClass}`}>{`${percentage} %`}</div>
                    </div>
                </>
            ) : (
                <div className="text-size-14 text-color-darkest margin-top-10 margin-bottom-10">
                    <span className="rioglyph rioglyph-dangerousgoods margin-right-5" />
                    <FormattedMessage id="intl-msg:engineOil.card.ssSingle.banner.tge.notSupported" />
                </div>
            )}
        </BasePanel>
    );
};

export default Ocu3SsSingleCard;
